import React, { useState, useRef, useEffect } from 'react';
import { Select, message } from 'antd';
import { getUsers } from '../../api/users';
import { apiErrorMessage } from '../../utils/apiError';

const { Option } = Select;

/**
 * Sunucu taraflı arama yapan kullanıcı seçici (öğretmen/veli/personel).
 * Büyüyen kullanıcı listelerinde tek seferde yüzlerce kayıt çekmek yerine
 * yazarken arama yapar (öğrenci seçicilerdeki örüntünün aynısı).
 */
const UserSearchSelect = ({
  role,
  branchId,
  value,
  onChange,
  placeholder = 'Aramak için en az 2 karakter yazın...',
  minChars = 2,
  extraParams,
  initialOption,
  initialOptions,
  renderLabel,
  // Varsayılan olarak genel /api/users araması kullanılır. Bazı ekranlarda
  // (ör. Rutin ataması) şube personeli + süper/developer admin gibi farklı
  // bir kapsam gerektiği için (ki bu kişilerin branch_id'si null olabiliyor)
  // özel bir arama fonksiyonu geçilebiliyor - o zaman role/branch_id yerine
  // sadece search + extraParams gönderilir, kapsam kararı sunucu tarafında verilir.
  fetchFn,
  ...rest
}) => {
  const seedOptions = initialOptions || (initialOption ? [initialOption] : []);
  const [options, setOptions] = useState(seedOptions);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const searchTimer = useRef(null);

  // initialOption(s) - tekli secim (duzenleme) veya coklu secim (mode=multiple,
  // duzenlenen kaydin mevcut secimlerini onceden gostermek) icin.
  useEffect(() => {
    const seed = initialOptions || (initialOption ? [initialOption] : []);
    setOptions((prev) => {
      const missing = seed.filter((o) => !prev.some((p) => p.id === o.id));
      return missing.length ? [...missing, ...prev] : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOption?.id, initialOptions]);

  const handleSearch = (text) => {
    clearTimeout(searchTimer.current);
    if (!text || text.trim().length < minChars) return;
    searchTimer.current = setTimeout(async () => {
      setLoading(true);
      setErrorText(null);
      try {
        const res = fetchFn
          ? await fetchFn({ search: text.trim(), ...extraParams })
          : await getUsers({
              search: text.trim(),
              role,
              branch_id: branchId || undefined,
              per_page: 20,
              ...extraParams,
            });
        const found = res.data?.data || res.data || [];
        // Secili kayitlari listede tut - aksi halde coklu secimde (mode=multiple)
        // baska bir arama yapilinca onceden secilmis kisi listeden dusuyor ve
        // etiketi kaybolup ham ID olarak gorunuyordu.
        setOptions((prev) => {
          const selectedIds = Array.isArray(value) ? value : (value != null ? [value] : []);
          const keep = prev.filter(
            (p) => selectedIds.includes(p.id) && !found.some((f) => f.id === p.id)
          );
          return [...keep, ...found];
        });
      } catch (error) {
        // Hata eskiden tamamen yutuluyordu: sunucu 403/422 donse bile kutu
        // sadece "bulunamadi" gosteriyor, kullanici sebebini goremiyordu
        // (ag sekmesinde yanit var ama ekranda yok). Artik sunucunun mesaji
        // hem uyari olarak hem de kutunun icinde gosteriliyor.
        setOptions([]);
        const text = apiErrorMessage(error, 'Kullanıcı araması başarısız oldu.');
        setErrorText(text);
        message.error(text);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  return (
    <Select
      showSearch
      value={value}
      onChange={onChange}
      onSearch={handleSearch}
      filterOption={false}
      loading={loading}
      placeholder={placeholder}
      notFoundContent={
        loading
          ? 'Aranıyor...'
          : errorText
            ? <span style={{ color: '#ff4d4f' }}>{errorText}</span>
            : 'Aramak için en az 2 karakter yazın'
      }
      {...rest}
    >
      {options.map((u) => (
        <Option key={u.id} value={u.id}>
          {renderLabel ? renderLabel(u) : `${u.name} ${u.surname}${u.phone ? ` — ${u.phone}` : ''}`}
        </Option>
      ))}
    </Select>
  );
};

export default UserSearchSelect;
